import { ItemRepository } from '../../domain/repositories/ItemRepository'
import { CreateUpdateItemModel } from '../models/CreateUpdateItemModel'
import type { ItemFilterEntity } from '../../domain/entities/ItemsFilterEntity'
import { ItemFilterModel } from '../models/ItemFilterModel'
import type { CreateUpdateItemEntity } from '../../domain/entities/CreateUpdateItemEntity'
import { ItemResponseModel } from '../models/ItemResponseModel'
import type { ItemResponseEntity } from '../../domain/entities/ItemResponseEntity'
import type { ItemEntity } from '../../domain/entities/ItemEntity'
import { ItemModel } from '../models/ItemModel'
import { BranchMiniModel } from '@/features/branch/data/models/BranchMiniModel'
import { getBranchByIdCase } from '@/features/branch/domain/usecases/getBranchByIdUsecase'
import { CategoryMiniModel } from '@/features/category/data/models/CategoryMiniModel'
import { getCategoryByIdCase } from '@/features/category/domain/usecases/getCategoryByIdUsecase'
import type { ItemAttributeEntity } from '../../domain/entities/ItemAttributeEntity'
import { CreateUpdateItemAttributeModel } from '../models/CreateUpdateItemAttribute'
import { ItemAttributeModel } from '../models/ItemAttributeModel'
import { MarketItemModel } from '../models/MarketItemModel'
import { Language } from '@/shared/enums/languagesEnum'
import type { VenderTranslationEntity } from '@/features/vender/domain/entities/VenderTrasnlationEntity'
import {
  ItemsService,
  OrdersService,
  SelectionsService,
  type PostItemAdminRequest,
  type PostPutSelectionAdminRequest,
  type PutItemAdminRequest,
  type SelectionAdminDto
} from '@/backend/market'
import type { MealTierItemEntity } from '../../domain/entities/MealTierItemEntity'
import { MealTierItemModel } from '../models/MealTierItemModel'

export class ItemRepositoryImpl implements ItemRepository {
  async getItems(filterData: ItemFilterEntity): Promise<ItemResponseEntity | { error: true }> {
    const itemsFilterModel = new ItemFilterModel(filterData)
    if (!itemsFilterModel.sortField) {
      itemsFilterModel.sortField = 'order'
      itemsFilterModel.sortDescending = false
    }
    try {
      const response = await ItemsService.getItemsAdmin(itemsFilterModel)
      const itemResponseModel = new ItemResponseModel(response as ItemResponseEntity)
      return itemResponseModel
    } catch {
      return { error: true }
    }
  }

  async deleteItem(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await ItemsService.deleteItemAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getItemById(id: number): Promise<ItemEntity | { error: true }> {
    try {
      const response = await ItemsService.getItemAdmin({
        id
      })

      const branchResponse = await getBranchByIdCase(response.branchId!)
      branchResponse.name =
        branchResponse.translations?.find(
          (translation: VenderTranslationEntity) => translation.language === Language.EN
        )?.name ?? ''
      const branchResponseModel = new BranchMiniModel(branchResponse)

      const categoryResponse = await getCategoryByIdCase(response.categoryId!)
      categoryResponse.name =
        categoryResponse.translations?.find(
          (translation: VenderTranslationEntity) => translation.language === Language.EN
        )?.name ?? ''
      const categoryResponseModel = new CategoryMiniModel(categoryResponse)

      const itemModel = new ItemModel(response, branchResponseModel, categoryResponseModel)
      return itemModel
    } catch {
      return { error: true }
    }
  }

  async createItem(data: CreateUpdateItemEntity): Promise<{ success: true } | { error: true }> {
    const createUpdateItemModel = new CreateUpdateItemModel(data)
    try {
      await ItemsService.postItemAdmin({
        requestBody: createUpdateItemModel as PostItemAdminRequest
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async updateItem(
    id: number,
    data: CreateUpdateItemEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateItemModel = new CreateUpdateItemModel(data)
    try {
      await ItemsService.putItemAdmin({
        id,
        requestBody: createUpdateItemModel as PutItemAdminRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async updateItemAttribute(
    id: number,
    itemData: ItemAttributeEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      const selections = itemData.map((item) => new CreateUpdateItemAttributeModel(item))
      const result = selections.map((item) => {
        return {
          ...item,
          order: item.order
        }
      })
      await SelectionsService.putSelectionByItemIdAdmin({
        itemId: id,
        requestBody: {
          selections: result as PostPutSelectionAdminRequest[]
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getItemAttribute(itemId: number): Promise<ItemAttributeModel[] | { error: true }> {
    try {
      const response = await SelectionsService.getSelectionsByItemIdAdmin({
        itemId
      })
      const itemAttributeModels = response.selections?.map(
        (attribute) => new ItemAttributeModel(attribute as SelectionAdminDto)
      )

      return itemAttributeModels ?? []
    } catch {
      return { error: true }
    }
  }

  async deleteItemAttribute(id: string): Promise<{ success: true } | { error: true }> {
    try {
      await SelectionsService.deleteSelectionAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateItemOrder(
    id: number,
    newOrder: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await ItemsService.putItemOrderAdmin({
        id,
        requestBody: {
          order: newOrder
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateItemGalleryOrder(
    id: string,
    newOrder: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await ItemsService.putItemGalleryOrderAdmin({
        id,
        newOrder
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async syncItems(branchId: number): Promise<{ success: true } | { error: true }> {
    try {
      await ItemsService.syncItemsAdmin({
        branchId
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getMarketOrderItems(
    id: string
  ): Promise<{ branchNote?: string; driverNote?: string; items: ItemEntity[] } | { error: true }> {
    try {
      const response = await OrdersService.getOrderItemsAdmin({
        id
      })
      const userModel = response.orderItems?.map((item) => new MarketItemModel(item))

      return {
        items: userModel as ItemEntity[],
        branchNote: response.branchNote ?? '',
        driverNote: response.driverNote ?? ''
      }
    } catch {
      return { error: true }
    }
  }

  async getItemMealTiers(id: number): Promise<MealTierItemEntity[] | { error: true }> {
    try {
      const response = await ItemsService.getItemMealTiersAdmin({
        id
      })
      return response.mealTiers?.map((mealTier) => new MealTierItemModel(mealTier)) ?? []
    } catch {
      return { error: true }
    }
  }

  async updateItemMealTiers(
    id: number,
    data: MealTierItemEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await ItemsService.putItemMealTiersAdmin({
        id: id,
        requestBody: {
          mealTierIds: data.map((mealTier) => mealTier.id).filter((id) => id !== undefined)
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
